/**
 * Rasterises the motif into a Canvas2D "paint layer" that the renderer samples as a texture.
 *
 * The paint layer is a window onto the painted plane around the rotation domain W (in disk
 * coordinates). A stroke painted once appears at every image γ(stroke), γ in the stroke's
 * symmetry group, so each stroke is drawn once per group element whose image of the stroke can
 * reach the window. That is what makes strokes continue seamlessly across mirrors and across
 * the edges of W, and lets a thick brush spill over an edge into the neighbouring copy.
 *
 * Colour encoding (premultiplied, sampled with linear filtering):
 *   ink → (1,0,0,1)  vermilion → (0,1,0,1)  gold → (0,0,1,1)  ivory → (0,0,0,1)
 * so the shader reads role coverages as (r, g, b, a − r − g − b).
 */
import type { Complex } from '../geometry/complex';
import { type Isometry, IDENTITY, apply, compose } from '../geometry/mobius';
import type { TriangleGroup } from '../geometry/triangle-group';
import { type Box, domainBox, domainScale, fromDomain } from './domain';
import { ERASER, type Motif, type Stroke } from './motif';

export const ROLE_CSS = ['#ff0000', '#00ff00', '#0000ff', '#000000'] as const;

/** Largest brush radius plus capture slack, as a fraction of the domain scale. */
const REACH = 0.75;
/** Subdivide stroke segments so no segment is longer than this (disk units) before mapping. */
const MAX_SEGMENT = 0.006;

export interface Paint2D {
  canvas: { width: number; height: number };
  save(): void;
  restore(): void;
  beginPath(): void;
  moveTo(x: number, y: number): void;
  lineTo(x: number, y: number): void;
  arc(x: number, y: number, r: number, a0: number, a1: number, ccw?: boolean): void;
  closePath(): void;
  fill(): void;
  clearRect(x: number, y: number, w: number, h: number): void;
  fillStyle: unknown;
  globalCompositeOperation: string;
}

interface Disc {
  x: number;
  y: number;
  r: number;
}

function circumcircle(a: Complex, b: Complex, c: Complex): Disc {
  const d = 2 * (a.re * (b.im - c.im) + b.re * (c.im - a.im) + c.re * (a.im - b.im));
  const a2 = a.re * a.re + a.im * a.im;
  const b2 = b.re * b.re + b.im * b.im;
  const c2 = c.re * c.re + c.im * c.im;
  const x = (a2 * (b.im - c.im) + b2 * (c.im - a.im) + c2 * (a.im - b.im)) / d;
  const y = (a2 * (c.re - b.re) + b2 * (a.re - c.re) + c2 * (b.re - a.re)) / d;
  return { x, y, r: Math.hypot(a.re - x, a.im - y) };
}

/** Image of a Euclidean disc (inside the unit disk) under a disk isometry: again a disc. */
export function imageDisc(g: Isometry, d: Disc): Disc {
  const p = (t: number) => apply(g, { re: d.x + d.r * Math.cos(t), im: d.y + d.r * Math.sin(t) });
  return circumcircle(p(0), p((2 * Math.PI) / 3), p((4 * Math.PI) / 3));
}

const discMeetsBox = (d: Disc, b: Box): boolean => {
  const cx = Math.max(b.x0, Math.min(d.x, b.x1));
  const cy = Math.max(b.y0, Math.min(d.y, b.y1));
  return Math.hypot(d.x - cx, d.y - cy) <= d.r;
};

/**
 * All group elements γ such that γ(D) meets `box`, where D is a disc containing every stroke
 * (W padded by the reach). Breadth-first search over the Cayley graph, right-multiplying by the
 * three reflections (the copies γ·m_i·F are the neighbours of γ·F).
 */
export function nearElements(tg: TriangleGroup, box: Box, reach: number): Isometry[] {
  const wb = domainBox(tg, 0);
  const D: Disc = {
    x: (wb.x0 + wb.x1) / 2,
    y: (wb.y0 + wb.y1) / 2,
    r: Math.hypot(wb.x1 - wb.x0, wb.y1 - wb.y0) / 2 + reach,
  };
  const w = box.x1 - box.x0;
  const h = box.y1 - box.y0;
  const loose: Box = { x0: box.x0 - w, y0: box.y0 - h, x1: box.x1 + w, y1: box.y1 + h };
  const keyOf = (g: Isometry) => {
    const z = apply(g, { re: 0.0123, im: 0.0071 });
    return `${g.flip ? 1 : 0}:${Math.round(z.re * 1e7)}:${Math.round(z.im * 1e7)}`;
  };
  const out: Isometry[] = [];
  const seen = new Set<string>([keyOf(IDENTITY)]);
  const queue: Isometry[] = [IDENTITY];
  while (queue.length > 0 && seen.size < 6000) {
    const g = queue.shift()!;
    const img = imageDisc(g, D);
    if (discMeetsBox(img, box)) out.push(g);
    if (!discMeetsBox(img, loose)) continue;
    for (const m of tg.mirrors) {
      const n = compose(g, m);
      const k = keyOf(n);
      if (seen.has(k)) continue;
      seen.add(k);
      queue.push(n);
    }
  }
  return out;
}

export interface RasterOptions {
  maxSide: number;
}

export class MotifRaster {
  readonly tg: TriangleGroup;
  readonly box: Box;
  readonly width: number;
  readonly height: number;
  readonly scale: number;
  private readonly nearAll: Isometry[];
  private readonly nearRot: Isometry[];
  private readonly ctx: Paint2D;
  /** Disk-unit radius of the full brush for size = 1. */
  private readonly unit: number;

  constructor(tg: TriangleGroup, ctx: Paint2D, opts: RasterOptions) {
    this.tg = tg;
    this.ctx = ctx;
    const b = domainBox(tg, 0);
    const pad = 0.02 * Math.max(b.x1 - b.x0, b.y1 - b.y0);
    this.box = { x0: b.x0 - pad, y0: b.y0 - pad, x1: b.x1 + pad, y1: b.y1 + pad };
    const bw = this.box.x1 - this.box.x0;
    const bh = this.box.y1 - this.box.y0;
    this.scale = opts.maxSide / Math.max(bw, bh);
    this.width = Math.max(16, Math.round(bw * this.scale));
    this.height = Math.max(16, Math.round(bh * this.scale));
    ctx.canvas.width = this.width;
    ctx.canvas.height = this.height;
    this.unit = domainScale(tg);
    const near = nearElements(tg, this.box, REACH * this.unit);
    this.nearAll = near;
    this.nearRot = near.filter((g) => !g.flip);
  }

  get elementCount(): { all: number; rotation: number } {
    return { all: this.nearAll.length, rotation: this.nearRot.length };
  }

  clear(): void {
    this.ctx.clearRect(0, 0, this.width, this.height);
  }

  rebuild(motif: Motif): void {
    this.clear();
    for (const s of motif.strokes) this.drawStroke(s);
  }

  /** Draws a whole stroke. */
  drawStroke(s: Stroke): void {
    this.drawRange(s, 0, s.points.length - 1);
  }

  /** Draws the part of a stroke between point indices i0 and i1 (inclusive). */
  drawRange(s: Stroke, i0: number, i1: number): void {
    if (s.points.length === 0) return;
    i0 = Math.max(0, i0);
    i1 = Math.min(s.points.length - 1, i1);
    // Resample into disk coordinates with per-point half-widths in disk units.
    const pts: Complex[] = [];
    const hw: number[] = [];
    const halfBase = 0.5 * s.size * this.unit;
    for (let i = i0; i <= i1; i++) {
      const z = fromDomain(this.tg, s.points[i]);
      const wf = s.widths ? s.widths[i] : 1;
      if (i > i0) {
        const prev = pts[pts.length - 1];
        const steps = Math.ceil(Math.hypot(z.re - prev.re, z.im - prev.im) / MAX_SEGMENT);
        const pa = s.points[i - 1];
        const pb = s.points[i];
        const wa = s.widths ? s.widths[i - 1] : 1;
        for (let k = 1; k < steps; k++) {
          const t = k / steps;
          pts.push(
            fromDomain(this.tg, { a: pa.a + (pb.a - pa.a) * t, b: pa.b + (pb.b - pa.b) * t }),
          );
          hw.push(halfBase * (wa + (wf - wa) * t));
        }
      }
      pts.push(z);
      hw.push(halfBase * wf);
    }
    // Bounding disc of the piece (for culling).
    let mx = 0;
    let my = 0;
    for (const p of pts) {
      mx += p.re;
      my += p.im;
    }
    mx /= pts.length;
    my /= pts.length;
    let rad = 0;
    let maxHw = 0;
    for (let i = 0; i < pts.length; i++) {
      rad = Math.max(rad, Math.hypot(pts[i].re - mx, pts[i].im - my));
      maxHw = Math.max(maxHw, hw[i]);
    }
    const disc: Disc = { x: mx, y: my, r: rad + maxHw * 1.3 + 1e-6 };
    const elements = s.symmetry === 'rotation' ? this.nearRot : this.nearAll;
    const ctx = this.ctx;
    ctx.save();
    if (s.role === ERASER) {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.fillStyle = '#000000';
    } else {
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = ROLE_CSS[s.role];
    }
    for (const g of elements) {
      if (!discMeetsBox(imageDisc(g, disc), this.box)) continue;
      ctx.beginPath();
      this.addCapsules(g, pts, hw);
      ctx.fill();
    }
    ctx.restore();
  }

  /**
   * Adds the union of discs at the points and the hulls joining consecutive discs, all wound in
   * the same direction so the non-zero fill rule paints their union without seams.
   * The brush is a hyperbolic disc: its Euclidean radius at an image point is scaled by the
   * isometry's local stretch (1 − |γz|²)/(1 − |z|²).
   */
  private addCapsules(g: Isometry, pts: Complex[], hw: number[]): void {
    const ctx = this.ctx;
    const n = pts.length;
    const X = new Float64Array(n);
    const Y = new Float64Array(n);
    const Rr = new Float64Array(n);
    for (let i = 0; i < n; i++) {
      const z = pts[i];
      const gz = apply(g, z);
      const stretch = (1 - (gz.re * gz.re + gz.im * gz.im)) / (1 - (z.re * z.re + z.im * z.im));
      X[i] = (gz.re - this.box.x0) * this.scale;
      Y[i] = (gz.im - this.box.y0) * this.scale;
      Rr[i] = Math.max(0.35, hw[i] * stretch * this.scale);
    }
    for (let i = 0; i < n; i++) {
      ctx.moveTo(X[i] + Rr[i], Y[i]);
      ctx.arc(X[i], Y[i], Rr[i], 0, 2 * Math.PI, false);
      ctx.closePath();
    }
    for (let i = 1; i < n; i++) {
      const dx = X[i] - X[i - 1];
      const dy = Y[i] - Y[i - 1];
      const d = Math.hypot(dx, dy);
      if (d < 1e-6) continue;
      const ux = dx / d;
      const uy = dy / d;
      // outer tangent of two circles (radii r0, r1): offset direction rotated from the normal
      const r0 = Rr[i - 1];
      const r1 = Rr[i];
      const s = Math.max(-1, Math.min(1, (r0 - r1) / d));
      const c = Math.sqrt(1 - s * s);
      // normal pointing left of travel, tilted by the radius difference
      const nlx = -uy * c + ux * s;
      const nly = ux * c + uy * s;
      const nrx = uy * c + ux * s;
      const nry = -ux * c + uy * s;
      const quad: [number, number][] = [
        [X[i - 1] + nlx * r0, Y[i - 1] + nly * r0],
        [X[i] + nlx * r1, Y[i] + nly * r1],
        [X[i] + nrx * r1, Y[i] + nry * r1],
        [X[i - 1] + nrx * r0, Y[i - 1] + nry * r0],
      ];
      // same orientation as the arcs (positive signed area in this y-down/y-up agnostic sense)
      let area = 0;
      for (let k = 0; k < 4; k++) {
        const [x0, y0] = quad[k];
        const [x1, y1] = quad[(k + 1) % 4];
        area += x0 * y1 - x1 * y0;
      }
      if (area < 0) quad.reverse();
      ctx.moveTo(quad[0][0], quad[0][1]);
      for (let k = 1; k < 4; k++) ctx.lineTo(quad[k][0], quad[k][1]);
      ctx.closePath();
    }
  }
}
